<script>
  import { Layer } from 'svelte-canvas';
  import { onMount } from 'svelte';
  import load from 'load-asset';
  import lerp from 'lerp';
  import { selectedLayout, foregroundPosition, startingDay, month, year, showCalendar, width, height, showCockerel } from '../stores.js';
  import { months, days, daysInMonth } from '../date.utils.js';
  import { fixtures } from '../data/fixtures.js';

  let emblem;
  let cockerel;

  onMount(async () => {
    emblem = await load('/assets/crests/emblem-w.svg');
    cockerel = await load('/assets/crests/cockerel-w.svg');
    // load club images
    for (let i = 0; i < fixtures.length; i++) {
      fixtures[i].image = await load(fixtures[i].filePath);
    }
  });


  // Cockerel
  // ---
  // cockerel position
  const emblemDimensions = [114, 235];
  const cockerelDimensions = [75, 187];
  // only used it rendering the desktop layout
  const gapFromCockerelToCalendar = 45;


  // Calendar
  // ---
  const clubShieldSize = [23, 23];

  // Typography
  const monthYearFontSize = 15;
  const daysOfWeekFontSize = 9;
  const dayofMonthFontSize = 8;

  // Positioning
  // start with Month Year position
  const monthYearPosition = [0, 0];
  // move down and offset the x position of the rest of the calendar to account
  // for the center text alignment, +9, +34
  const weekdaysPosition = [9, 34];
  // move down another 30 pixels, 34+30
  const calendarDaysPosition = [9, 64];
  // width, height of calendar days (1–30)
  const calendarDaysDimensions = [280, 144];
  const dayPoints = [];
  // populate dayPoints
  for (let c = 0; c < 7; c++) {
    const u = c / (7 - 1);
    const x = lerp(weekdaysPosition[0], weekdaysPosition[0] + calendarDaysDimensions[0], u);
    const y = weekdaysPosition[1];

    dayPoints.push([x, y]);
  }

  // do not out a number if that day has a game
  // -> Nottn. Forest has a clear section so you can see the number in the back
  function dayHasGame(day) {
    let hasGame = false;
    // loop through fixtures array and compare the date
    for (let i = 0; i < fixtures.length; i++) {
      if (day == fixtures[i].day) {
        hasGame = true;
        break;
      }
    }
    return hasGame;
  }

  function determineCoordinates(position) {
    const coordinates = [
      // bottom-left
      [55, -300],
      // bottom-right
      [-530, -300],
      // top-right
      [-530, 55],
      // top-left
      [55, 55]
    ];
    let x, y;

    if (position == 0) {
      x = 0 + coordinates[position][0];
      y = $height + coordinates[position][1];
    }
    if (position == 1) {
      x = $width + coordinates[position][0];
      y = $height + coordinates[position][1];
    }
    if (position == 2) {
      x = $width + coordinates[position][0];
      y = 0 + coordinates[position][1];
    }
    if (position == 3) {
      x = 0 + coordinates[position][0];
      y = 0 + coordinates[position][1];
    }

    return [x, y];
  }


  // Render function
  // ---
  $: render = ({ context, width, height }) => {

    // // return if we're not drawing the calendar
    // if (!$showCalendar) return;

    // draw the calendar
    let offsetXY = [0, 0];
    let calendarOffset = [0, 0];

    if ($selectedLayout === 0 || $selectedLayout === 1) {
      calendarOffset = [
        (width - calendarDaysDimensions[0])/2 - 10,
        215
      ];
    }
    if ($selectedLayout === 2) {
      calendarOffset = [
        (width - calendarDaysDimensions[0])/2 - 10,
        height - (calendarDaysDimensions[1] * 2.75)
      ];
    }
    if ($selectedLayout === 3) {
      offsetXY = determineCoordinates($foregroundPosition);
      calendarOffset = [
        offsetXY[0] + emblemDimensions[0] + gapFromCockerelToCalendar,
        offsetXY[1] + 9
      ];
      // if (!$showCalendar && ($foregroundPosition === 1 || $foregroundPosition === 2)) {
      if (!$showCockerel && ($foregroundPosition === 0 || $foregroundPosition === 3)) {
        calendarOffset[0] -= (emblemDimensions[0] + gapFromCockerelToCalendar);
        // calendarOffset[0] += 9
      }
    }


    // // draw a rectangle to debug the sizing
    // context.fillStyle = 'hsla(227, 64%, 21%, 0.5)';
    // context.fillRect(
    //   offsetXY[0], offsetXY[1],
    //   cockerelDimensions[0] + gapFromCockerelToCalendar + calendarDaysDimensions[0],
    //   cockerelDimensions[1]);


    // calendar
    // ======
    if ($showCalendar) {
      // month year, MARCH 2022
      // ---
      context.fillStyle = 'white';
      context.font = `600 ${monthYearFontSize}px founders-grotesk`;
      context.textAlign = 'left';

      // only show month year on desktop
      if ($selectedLayout === 3) {
        context.fillText(`${months[$month].toUpperCase()} ${$year}`, monthYearPosition[0] + calendarOffset[0], monthYearPosition[1] + calendarOffset[1]);
      }


      // days of week
      // ---
      context.font = `600 ${daysOfWeekFontSize}px founders-grotesk`;
      context.textAlign = 'center';

      for (let i = 0; i < days.length; i++) {
        let dayIndex = $startingDay + i;

        if (dayIndex >= days.length) {
          dayIndex = dayIndex - days.length;
        }

        context.fillText(days[dayIndex][0].toUpperCase(), dayPoints[i][0] + calendarOffset[0], dayPoints[i][1] + calendarOffset[1]);
      }


      // draw days of month
      // ---
      context.font = `400 ${dayofMonthFontSize}px founders-grotesk-mono`;
      context.textAlign = 'right';

      const textMetric = context.measureText('33');

      const calendar = [];

      // create date object by taking: year, month, day
      const thisMonth = new Date($year, $month, 1);

      // offset is used to determine when the month's days start and how many days
      // from the previous month overlap
      let offset = thisMonth.getDay() - $startingDay;

      // if offset is less than 0, add another week
      if (offset < 0) {
        offset += days.length;
      }

      // get total number of days in this month
      const totalDaysInMonth = daysInMonth($month+1, $year);
      // console.log(totalDaysInMonth);

      // how many rows (weeks) do we need?
      const numberOfRows = Math.ceil((offset + totalDaysInMonth) / 7);
      // console.log(numberOfRows);

      // like offset, but for the overlapping days of the next month
      const remainder = (numberOfRows * 7) - (offset + totalDaysInMonth);
      // console.log(remainder);

      // previous month's overlapping days
      const daysInLastMonth = daysInMonth($month, $year);
      // console.log(daysInLastMonth);

      // make grid points, 6 rows of 7 columns
      const columns = days.length;
      let i = 0;
      for (let r = 0; r < numberOfRows; r++) {
        for (let c = 0; c < columns; c++) {
          const u = c / (columns - 1);
          const v = r / (numberOfRows - 1);
          const x = lerp(calendarDaysPosition[0], calendarDaysPosition[0] + calendarDaysDimensions[0], u);
          const y = lerp(calendarDaysPosition[1], calendarDaysPosition[1] + calendarDaysDimensions[1], v); // + 30
          const dayObject = {
            day: '0',
            position: [x, y],
            fullOpacity: false
          }

          if (i < offset) {
            // total days in last month minus the i
            dayObject.day = `${daysInLastMonth + 1 - (offset - i)}`;
          } else if (i < offset + totalDaysInMonth) {
            dayObject.day = i + 1 - offset;
            dayObject.fullOpacity = true;
          } else {
            dayObject.day = i + 1 - (offset + totalDaysInMonth);
          }

          calendar.push(dayObject);
          i++;
        }
      }


      for (let i = 0; i < calendar.length; i++) {
        context.fillStyle = 'hsla(0, 100%, 100%, 1)';
        if (calendar[i].fullOpacity) {
          context.globalAlpha = 1;
        } else {
          context.globalAlpha = 0.2;
        }

        // if the day has a game, don't output a number
        if (!calendar[i].fullOpacity || !dayHasGame(calendar[i].day)) {
          context.fillText(
            `${calendar[i].day}`,
            calendar[i].position[0] + (textMetric.width/2) + calendarOffset[0],
            calendar[i].position[1] + (textMetric.actualBoundingBoxAscent/2) + calendarOffset[1]
          );
        }

        // // grid area
        // context.fillStyle = 'hsla(0, 100%, 100%, 0.2)';
        // context.fillRect(datePoints[i].position[0] - (clubShieldSize[0]/2), datePoints[i].position[1] - (clubShieldSize[1]/2), clubShieldSize[0], clubShieldSize[1]);

        // check if there's a game on this day
        for (let f = 0; f < fixtures.length; f++) {
          // TODO: clean up this if statement
          if (calendar[i].day === fixtures[f].day && fixtures[f].image && calendar[i].fullOpacity) {
            context.drawImage(
              fixtures[f].image,
              (calendar[i].position[0] + calendarOffset[0]) - (clubShieldSize[0]/2),
              (calendar[i].position[1] + calendarOffset[1]) - (clubShieldSize[1]/2),
              clubShieldSize[0], clubShieldSize[1]
            );
          }
        }
      }
      context.globalAlpha = 1;
    }

    // draw cockerel
    // ---
    if ($showCockerel) {
      // Mobile
      if ($selectedLayout === 0 && emblem) {
        context.globalAlpha = 0.13;
        context.drawImage(emblem, (width - 317)/2, (height-653)/2, 317, 653);
        context.globalAlpha = 1;
      }
      // Mobile 2
      if ($selectedLayout === 1 && cockerel) {
        const scale = 3.8;
        const cw = cockerelDimensions[0] * scale;
        const ch = cockerelDimensions[1] * scale;
        context.globalAlpha = 0.13;
        context.drawImage(cockerel, width - (cw/2), (height-ch)/2, cw, ch);
        context.globalAlpha = 1;
      }
      // Mobile 3
      if ($selectedLayout === 2 && cockerel) {
        const scale = 1;
        const cw = cockerelDimensions[0] * scale;
        const ch = cockerelDimensions[1] * scale;
        // context.globalAlpha = 0.13;
        context.drawImage(cockerel, (width/2) - (cw/2.25), height/2 - ch, cw, ch);
        // context.drawImage(emblem, (width - 317)/2, (height-653)/2, 317, 653);
        // context.globalAlpha = 1;
      }

      // Desktop
      if ($selectedLayout === 3 && emblem) {
        let x = offsetXY[0];
        const y = offsetXY[1];
        if (!$showCalendar && ($foregroundPosition === 1 || $foregroundPosition === 2)) {
          x += (calendarDaysDimensions[0] + gapFromCockerelToCalendar);
        }
        context.drawImage(emblem, x, y, emblem.width, emblem.height);
      }

    }
  };
</script>

<Layer {render}/>
